import type { ListBuilderList } from '../../../types';
import { LICENSE_STATUS_STYLE_MAP } from '../status-style-map';

/**
 * All Licenses list - unfiltered view with full filter bar.
 * This is the base list that other tabs inherit columns from.
 */
export const allLicensesList: ListBuilderList = {
	id: 'default-licenses-all',
	cacheTTL: 600000,
	name: 'All Licenses',
	type: 'default',
	active: true,
	// First, so it is the tab Current Licenses OPENS on: the page opens the
	// lowest-sorted active list (`defaultListId` is declared in the types but
	// nothing reads it). It used to open on Expiring Soon, which showed 1,431 of
	// 60,741 licenses under a menu item named Current Licenses; expiring
	// licenses now have the dashboard's card and tiles (Chris 2026-09-20).
	sortOrder: 1,
	filterRules: {
		// Opens on the licenses in use. A default, not a lock: removing the chip
		// shows every status.
		licenseStatus: {
			defaultValue: [{ id: 'Active', label: 'Active' }],
		},
	},
	columns: [
		{
			id: 'license-status',
			fields: [
				{
					id: 'status-field',
					dataConfig: {
						type: 'field',
						field: {
							// Badge shows the stored `client_license.status`. Expiry
							// is DERIVED, not stored: the Expired tab counts Active
							// rows with a past expirationDate as Expired, and those
							// rows carry the red date-derived "Expired" pill
							// (isExpired) beside this badge, so the tab population
							// and the row display stay coherent.
							fieldName: 'status',
							objectTable: 'client_license',
						},
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'status_map',
							statusMap: { ...LICENSE_STATUS_STYLE_MAP },
						},
					},
				},
				{
					// Disciplinary pill — folded into the Status cell so the
					// table doesn't waste a column on a flag that's clean
					// for the vast majority of licenses (rendering "-" in
					// every row was just visual noise). Stacks below the
					// status badge.
					//
					// PD-13955 Phase A: keys on `hasDisciplinaryRecord` —
					// actual recorded disciplinary actions (same predicate as
					// the At Risk tab and the dashboard KPI) — NOT the bare
					// registry flag; flag-only rows get the amber "Board
					// Flagged" pill below instead.
					//
					// Static red coloring (rather than `status_map`) because
					// the renderer's `evaluateColorRule` doesn't have a
					// branch for `status_map` — the existing Status badge
					// gets its colors via a different path that's tied to
					// field-value lookup. A `custom_text` data source would
					// not flow through that path, so we paint it
					// explicitly here.
					id: 'disciplinary-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Disciplinary',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'danger' },
						},
					},
					conditionalVisibility: {
						field: 'hasDisciplinaryRecord',
						condition: 'equals',
						value: true,
					},
				},
				{
					// PD-13955 Phase A: amber "Board Flagged" pill — the state
					// board asserts a disciplinary flag on the matched registry
					// record, but no detail has been recorded yet. Visually
					// distinct from the red recorded-actions pill above and
					// never counted in the At Risk KPI/tab. Mutually exclusive
					// with it server-side (isBoardFlagged = flag && !record).
					id: 'board-flagged-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Board Flagged',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'isBoardFlagged',
						condition: 'equals',
						value: true,
					},
				},
			],
			label: 'Status',
			headers: [{ id: 'h1', text: 'Status' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'status',
					objectTable: 'client_license',
				},
			],
			width: 96,
		},
		{
			id: 'license-number-type',
			fields: [
				{
					id: 'license-number-field',
					link: {
						type: 'object',
						enabled: true,
						objectTable: 'client_license',
					},
					dataConfig: {
						type: 'field',
						field: {
							// PD-16466: the server's display form of the number — a
							// CMEA self-certification has none and reads as a label.
							fieldName: 'licenseNumberLabel',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'md',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
				{
					id: 'line-break-1',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'line_break',
				},
				{
					// `typeLabel` is a server-projected display label for
					// the canonical LicenseSourceType stored in
					// `client_license.type` (STATE_BOARD → "State Board",
					// DEA → "DEA", KYC → "KYC", etc.) — see
					// LicensesService.findAll. The list column reads this
					// derived field so the user never sees the raw
					// SCREAMING_SNAKE_CASE storage form.
					id: 'type-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'typeLabel',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
				},
				{
					id: 'line-break-2',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'line_break',
				},
				{
					// PD-14843: the state's sub-type classification (FL "Special
					// Sterile Compounding" vs "Community Pharmacy, Schedule II &
					// III") — what makes two same-type licenses on one client
					// distinguishable at a glance. Hidden when the state
					// publishes no finer classification.
					id: 'subtype-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'description',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
						textExpansion: { enabled: true, maxLines: 1 },
					},
					displayStyle: 'text',
					conditionalVisibility: {
						field: 'description',
						condition: 'is_not_null',
					},
				},
			],
			label: 'License #',
			headers: [{ id: 'h2', text: 'License #' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'licenseNumber',
					objectTable: 'client_license',
				},
			],
			width: 144,
		},
		{
			id: 'license-client',
			fields: [
				{
					id: 'lead-customer-badge',
					floatingLeft: true,
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientLeadOrCustomer',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'value-mapping' },
						valueMapping: [
							{
								value: 'Customer',
								displayText: 'C',
								color: { type: 'info' },
							},
							{
								value: 'Lead',
								displayText: 'L',
								color: { type: 'warning' },
							},
						],
					},
					displayStyle: 'badge',
				},
				{
					id: 'client-name-field',
					link: {
						type: 'custom',
						enabled: true,
						urlTemplate: '/clients/{clientId}',
					},
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientName',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'theme',
							themeColor: 'statusInfo',
						},
					},
					displayStyle: 'text',
				},
				{
					// PD-16670: a closed account (the shared closed-account rule,
					// computed by the API) — shown, never hidden, so a reviewer
					// can match it up.
					id: 'closed-account-badge',
					dataConfig: { type: 'custom_text', customText: 'Closed account' },
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'neutral' },
						},
					},
					conditionalVisibility: {
						field: 'isClosedAccount',
						condition: 'equals',
						value: true,
					},
				},
				{
					id: 'line-break-1',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'address-badge-spacer-1',
					floatingLeft: true,
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'default' },
					},
					displayStyle: 'badge',
				},
				{
					id: 'client-street-address',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientStreetAddress',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
				},
				{
					id: 'line-break-2',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'address-badge-spacer-2',
					floatingLeft: true,
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'default' },
					},
					displayStyle: 'badge',
				},
				{
					id: 'client-city-state-zip',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientCityStateZip',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
				},
			],
			label: 'Client',
			headers: [{ id: 'h-client', text: 'Client' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'client_license',
				},
			],
			minWidth: 250,
		},
		// PD-15539: the single "State" column is retired. It rendered
		// `client_license.issuing_state`, which is written from TWO different
		// facts — the matched registry row's issuing board on confirm, and the
		// client's address by `_backfillIssuingState()` on every scan — so one
		// header answered with a different fact depending on the row (measured
		// 2026-08-28 on pharmsource: 12,505 registry-sourced vs 33,511
		// address-sourced of 46,061). Product decision 2026-08-24: issuing state
		// and client location are two different facts and must never be
		// conflated in a label, filter or coverage metric. Two columns follow.
		{
			// ONE column, two rows, two labels (Chris 2026-09-19): the header reads
			// "Located" over "Issued" and each cell shows the two state codes in
			// that order. Location leads because it is almost never blank (27 of
			// 60,741 on pharmsource staging) while the issuing state is blank on
			// about 69% of rows: unlinked licenses and federal registrations. They are still two separately named facts, never one
			// "State" — but as two columns their headers cost 258px for two
			// 2-letter codes and left the Client column unreadable at 1280.
			id: 'license-states',
			fields: [
				{
					id: 'client-location-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientLocationState',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
					emptyValue: '-',
				},
				{
					id: 'states-line-break',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'line_break',
				},
				{
					id: 'issuing-state-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'issuingStateCode',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
					// Blank is the honest value for an unmatched license or a federal
					// registration; the dash keeps the row present so the cell always
					// reads location over issuing state.
					emptyValue: '-',
				},
			],
			label: 'Located / Issued',
			headers: [
				{ id: 'h-client-location', text: 'Located' },
				{ id: 'h-issuing-state', text: 'Issued' },
			],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			// The issuing state is derived from the matched registry row AFTER
			// pagination (the batched overlay in LicensesService.findAll), so
			// MySQL cannot order by it. The client location can be ordered, and
			// that order lives in the sort list: a click on a two-label header
			// would not say which of the two it sorts.
			sortable: false,
			width: 84,
		},
		{
			id: 'license-expiration',
			fields: [
				{
					id: 'expiration-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'expirationDate',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'default' },
						// F18: 4-digit year format so legitimate year-2000
						// dates render "1/9/2000" instead of "1/9/00", which
						// previously looked like a parse bug.
						formatOptions: { dateFormat: 'mdy' },
					},
					displayStyle: 'date',
				},
				{
					// PD-13411: amber "Expiring Soon" pill under the date for
					// Active licenses inside the canonical 30-day window
					// (isExpiringSoon computed server-side via the shared
					// isLicenseExpiringSoon rule). Same stacked-conditional-pill
					// shape as the Disciplinary pill in the Status column,
					// including the static color rationale documented there.
					id: 'expiring-soon-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Expiring Soon',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'isExpiringSoon',
						condition: 'equals',
						value: true,
					},
				},
				{
					// PD-13411: red "Expired" pill once the expiration day has
					// passed — mutually exclusive with the amber pill by the
					// shared isLicenseExpired/isLicenseExpiringSoon rules.
					// Covers Active rows whose status hasn't flipped to Expired.
					id: 'expired-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Expired',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'danger' },
						},
					},
					conditionalVisibility: {
						field: 'isExpired',
						condition: 'equals',
						value: true,
					},
				},
			],
			label: 'Expires',
			headers: [{ id: 'h-expiration', text: 'Expires' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'expirationDate',
					objectTable: 'client_license',
				},
			],
			width: 112,
		},
		{
			// Files column — "2 attached · 1 verification": rep-attached documents
			// (`license__file`) and the registry evidence of the CONFIRMED
			// registry link, counted with the same query the Files tab's
			// Registry Evidence section uses (PD-14813), so the cell and the tab
			// agree. A pending suggestion's evidence is never counted: the tab
			// does not show it. `fileCountLabel` / `totalFileCount` are
			// projected by licenses.service.ts findAll; the sort key stays the
			// tenant-local attached count (PD-13891).
			//
			// Cell is clickable: navigates to the license's Files tab, which
			// lists both halves of the count (Uploaded Files + Registry
			// Evidence). It used to open the Verification (audit) tab, which
			// does not list uploads, so "1 attached" led to a page without it.
			id: 'license-file-count',
			fields: [
				{
					id: 'file-count-field',
					link: {
						type: 'custom',
						enabled: true,
						urlTemplate: '/licenses/{id}/files',
					},
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'fileCountLabel',
							objectTable: 'client_license',
						},
						defaultValue: '0',
					},
					styleConfig: {
						size: 'sm',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
			],
			label: 'Files',
			headers: [{ id: 'h-file-count', text: 'Files' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'DESC',
					fieldName: 'totalFileCount',
					objectTable: 'client_license',
				},
			],
			// Fixed: as a flexible column it absorbed ~300px for a one-digit count.
			// 72 fits the header and its sort arrows.
			width: 72,
		},
		{
			// Client gross sales, rolling 90 days over rolling 30 days
			// (client.rollingRevenue90 / rollingRevenue30, projected by
			// licenses.service.ts findAll). On a compliance list the dollars at
			// stake decide which account to call first (Chris 2026-09-18).
			// ONE two-line column, as the Clients list consolidates its figures:
			// as two 96px columns they left the Client column ~100px at 1440, with
			// account names and addresses cut mid-word (Chris 2026-09-19).
			id: 'license-client-sales',
			fields: [
				{
					id: 'client-sales-90-label',
					dataConfig: { type: 'custom_text', customText: '90d' },
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
				},
				{
					id: 'client-sales-90-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientRevenue90',
							objectTable: 'client_license',
						},
						defaultValue: '0',
					},
					styleConfig: {
						size: 'sm',
						weight: 'medium',
						colorRule: { type: 'default' },
						formatOptions: {
							numberFormat: 'currency',
							decimalPlaces: 0,
						},
					},
					displayStyle: 'currency',
				},
				{
					id: 'client-sales-line-break',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'line_break',
				},
				{
					id: 'client-sales-30-label',
					dataConfig: { type: 'custom_text', customText: '30d' },
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
					},
					displayStyle: 'text',
				},
				{
					id: 'client-sales-30-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'clientRevenue30',
							objectTable: 'client_license',
						},
						defaultValue: '0',
					},
					styleConfig: {
						size: 'xs',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
						formatOptions: {
							numberFormat: 'currency',
							decimalPlaces: 0,
						},
					},
					displayStyle: 'currency',
				},
			],
			label: 'Sales',
			headers: [{ id: 'h-client-sales', text: 'Sales' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			alignment: 'right',
			sortable: false,
			width: 112,
		},
		{
			id: 'license-actions',
			fields: [
				{
					id: 'actions-dropdown',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'actions_dropdown',
					dropdownActions: [
						{
							id: 'review-match-action',
							label: 'Review Match',
							iconName: 'clipboardCheck',
							callbackName: 'reviewMatch',
							variant: 'primary',
							visibility: 'visible',
							requiredPermission: 'licenses:update',
							// PD-13891: only surface "Review Match" on rows that
							// actually have a PENDING match to review. findAll sets
							// `matchConfidence` (the best pending-match score) only
							// for licenses with a pending_review candidate, so
							// is_not_null gates the action to exactly those rows.
							// (General match review now lives on the Pending Matches
							// page; this stays as a per-row shortcut where relevant.)
							// Mutually exclusive with the confirmed "View Match" action
							// below: a confirmed license has no pending candidate
							// (matchConfidence is null), so only one of the two shows.
							conditionalVisibility: {
								field: 'matchConfidence',
								condition: 'is_not_null',
							},
						},
						{
							// PD-13979 — for an already-CONFIRMED match, "Search" is
							// wrong (the license is already linked) and the pending
							// "Review Match" above never shows (no pending candidate
							// once confirmed). This action opens the license detail
							// Overview, where the Registry Data panel renders the
							// confirmed registry record with Preview-File + Unmatch —
							// the place to review or undo a confirmed match (the old
							// Matches tab was folded into it — PD-13985). Gated on the
							// confirmed-match FK; the matchmaker never re-suggests an
							// already-matched license, so this is mutually exclusive
							// with both the pending Review and the Search actions.
							id: 'review-matched-action',
							label: 'View Match',
							iconName: 'eye',
							callbackName: 'reviewMatched',
							variant: 'primary',
							visibility: 'visible',
							requiredPermission: 'licenses:update',
							conditionalVisibility: {
								field: 'fkRegistryLicenseId',
								condition: 'is_not_null',
							},
						},
						{
							// PD-13915 — per-row manual registry search, the same
							// affordance as the client Licenses tab. Opens the
							// individual match dialog scoped to THIS license. Shown ONLY
							// on genuinely unmatched rows: no confirmed registry link
							// AND no pending suggestion (PD-13979), so it is mutually
							// exclusive with both "Review Match" (pending) and "View
							// Match" (confirmed) above — a row shows exactly one. That
							// compound predicate can't be expressed by the single-field
							// conditionalVisibility, so it flows through the
							// `canSearchForMatches` visibility callback. The high-volume
							// save-and-next flow lives in the list's top-level
							// "Manually Match Licenses" action (PD-13916).
							id: 'search-for-matches-action',
							// "Match" — matches the "Pending Manual Match" tab/state
							// on the Matching page (PD-13998); the action opens the
							// registry search where the rep picks + saves the match.
							label: 'Match',
							iconName: 'magnifying-glass',
							callbackName: 'searchForMatches',
							variant: 'secondary',
							visibility: 'visible',
							requiredPermission: 'licenses:update',
							visibilityCallback: 'canSearchForMatches',
						},
						{
							id: 'edit-action',
							label: 'Edit',
							iconName: 'pen',
							callbackName: 'edit',
							variant: 'secondary',
							visibility: 'visible',
							requiredPermission: 'licenses:update',
						},
						{
							id: 'delete-action',
							label: 'Delete',
							iconName: 'trash',
							callbackName: 'delete',
							variant: 'danger',
							visibility: 'visible',
							requiredPermission: 'licenses:delete',
						},
					],
				},
			],
			label: '',
			headers: [{ id: 'h-actions', text: '' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			// Right-align so the trailing Edit/Delete icons stay flush-right and
			// line up across every row; "Review Match" slides in to their left
			// only on pending-match rows instead of pushing them out of alignment.
			alignment: 'right',
			sortable: false,
			// Room for all three icon buttons (Review/Search, Edit, Delete — 109px + padding); at 64
			// the row overflowed, clipped the buttons and forced a horizontal
			// scrollbar on a table that otherwise fit.
			width: 128,
		},
	],
	defaultSortId: 'column-license-expiration-asc',
	clickAction: {
		type: 'navigate',
		path: '/licenses/{id}',
	},
	actions: [
		{
			id: 'create-license',
			label: 'Create License',
			icon: 'plus',
			type: 'create',
			position: 'header',
		},
	],
};
